import { getBizChatWindowKey } from "owa-copilot-chat-drawer-store";
import { getMessageStateKey } from "./getMessageStateKey";
import { isBizchatContextDisabled } from "./isBizchatContextDisabled";
import { updateBizchatLoadedMapMutator } from "owa-sidepane-bizchat-store";
import { getMailboxInfoByAccountKey } from "owa-account-source-list-store";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import isPopout from "owa-popout-v2/lib/utils/isPopout";
import type { CallerInfo, IContextualChat } from "owa-bizchat-types/lib/types";
import type { IPromptInputData } from "owa-bizchat-types/lib/IPromptInputData";
import type { BizChatCompositeKey, WindowKey } from "owa-bizchat-types";
import { getAccountKeyForMailboxInfo, type AccountKey, type MailboxInfo } from "owa-client-types";
import { setComposeAgentModeActive } from "./agentModeState";

type WindowMessageState = {
    handler?: (message: unknown) => void;
    chatContext?: IContextualChat;
    promptInputData?: IPromptInputData;
    callerInfo?: CallerInfo;
    accountKey: AccountKey;
    windowKey: WindowKey;
};

/**
 * Map storing message handler state for each <window, account> pair.
 */
const messageStateMap: Map<BizChatCompositeKey, WindowMessageState> = new Map();

export function resetPostMessageHandler(mailboxInfo: MailboxInfo, targetWindow: Window, isImmersiveBizchat?: boolean): void {
    const state = getState(mailboxInfo, targetWindow, isImmersiveBizchat);
    if (state) {
        if (isPopout(targetWindow) ||
            !isGlobalFeatureEnabled("mon-copilot-bizchat-context-usecache-init")) {
            // Destroy state for popouts as well as caching flag off
            destroyState(mailboxInfo, targetWindow, isImmersiveBizchat);
        }
        else {
            // Keep chatContext for caching and any unsent prompt, clear others
            state.handler = undefined;
            state.callerInfo = undefined;
        }
    }
    setComposeAgentModeActive(targetWindow, false);
    
    updateBizchatLoadedMapMutator(false /* isLoaded */, mailboxInfo, getBizChatWindowKey(targetWindow, isImmersiveBizchat));
}

/**
 * Surgically clears only the registered post-message handler closure for a frame, without the
 * heavier teardown of `resetPostMessageHandler` (no compose-agent reset, no loaded-map update, no
 * cached chatContext/prompt destruction).
 *
 * Used when a BizChat iframe was unmounted while another surface (e.g. the Tasks Hub) was shown:
 * the stale handler still points at the now-dead frame. Clearing it forces the next
 * `openBizChatSidePane` onto the cold path (stash promptInputData + let the freshly-remounted
 * iframe pick up the conversationId) instead of posting into the dying frame on the hot path.
 */
export function invalidatePostMessageHandler(mailboxInfo: MailboxInfo, targetWindow: Window, isImmersiveBizchat?: boolean): void {
    const state = getState(mailboxInfo, targetWindow, isImmersiveBizchat);
    if (state) {
        state.handler = undefined;
    }
}

export function resetPostMessageHandlerForMainWindow(): void {
    for (const state of messageStateMap.values()) {
        // TODO: we need to separate owa-bizchat-shared to separate packages
        // so this main window check isn't duplicated
        if (state.windowKey === "SidePane" || state.windowKey === "Immersive") {
            const mailboxInfo = getMailboxInfoByAccountKey(state.accountKey);
            if (mailboxInfo) {
                resetPostMessageHandler(mailboxInfo, window);
            }
        }
    }
}

export function getIsPostMessageToFrameReady(mailboxInfo: MailboxInfo, targetWindow: Window): boolean {
    return getState(mailboxInfo, targetWindow)?.handler !== undefined;
}

export function initializePostMessageToFrameCallback(postMessageToFrameCallback: (message: unknown) => void, mailboxInfo: MailboxInfo, targetWindow: Window, isImmersiveBizchat?: boolean): void {
    const currentState = getOrCreateState(mailboxInfo, targetWindow, isImmersiveBizchat);
    if (currentState.handler !== postMessageToFrameCallback) {
        currentState.handler = postMessageToFrameCallback;
    }
    // We need to set any pending query annotations for Sydney if Chat wasn't ready yet
    if (currentState.chatContext !== undefined && !isBizchatContextDisabled()) {
        postContextualChatChangedMessage(currentState.chatContext, mailboxInfo, targetWindow);
    }
    
    if (currentState.callerInfo) {
        postUpdateCallerInfoMessage(mailboxInfo, targetWindow);
    }
}

export function postUpdatePromptInputDataMessage(mailboxInfo: MailboxInfo, targetWindow: Window, isImmersiveBizchat?: boolean): void {
    const currentState = getState(mailboxInfo, targetWindow, isImmersiveBizchat);
    const message = {
        func: "updatePromptInputData",
        args: [currentState?.promptInputData],
    };
    if (currentState) {
        currentState.handler?.(message);
        currentState.promptInputData = undefined; // Clear the prompt input data after sending it
    }
}

export function setPendingContext(context: IContextualChat, mailboxInfo: MailboxInfo, targetWindow: Window): void {
    const state = getOrCreateState(mailboxInfo, targetWindow);
    state.chatContext = context;
}

export function getPendingContext(mailboxInfo: MailboxInfo, targetWindow: Window): IContextualChat | undefined {
    return getState(mailboxInfo, targetWindow)?.chatContext;
}

/**
 * Internal function to send context changes to the BizChat frame.
 * Use setContextualChat instead for benefits like pending context handling.
 */
export function postContextualChatChangedMessage(contextualChat: IContextualChat, mailboxInfo: MailboxInfo, targetWindow: Window): void {
    if (isBizchatContextDisabled()) {
        return;
    }
    const message = {
        func: "contextualChatChanged",
        args: [contextualChat],
    };
    
    const currentState = getState(mailboxInfo, targetWindow);
    if (currentState) {
        currentState.handler?.(message);
        if (!isGlobalFeatureEnabled("mon-copilot-bizchat-context-usecache-init")) {
            currentState.chatContext = undefined; // Clear the chat context after sending it
        }
    }
    return;
}

export function setPromptInputData(newPromptInputData: IPromptInputData | undefined, mailboxInfo: MailboxInfo, targetWindow: Window, isImmersiveBizchat?: boolean): void {
    const state = getOrCreateState(mailboxInfo, targetWindow, isImmersiveBizchat);
    state.promptInputData = newPromptInputData;
}

export function getPromptInputData(mailboxInfo: MailboxInfo, targetWindow: Window): IPromptInputData | undefined {
    return getState(mailboxInfo, targetWindow)?.promptInputData;
}

export function setCallerInfo(callerInfo: CallerInfo, mailboxInfo: MailboxInfo, targetWindow: Window): void {
    if (isGlobalFeatureEnabled("cal-teams-copilot-hostname")) {
        setCallerInfoState(callerInfo, mailboxInfo, targetWindow);
        if (getIsPostMessageToFrameReady(mailboxInfo, targetWindow)) {
            postUpdateCallerInfoMessage(mailboxInfo, targetWindow);
        }
    }
}

function postUpdateCallerInfoMessage(mailboxInfo: MailboxInfo, targetWindow: Window): void {
    const { callerInfo, handler } = getState(mailboxInfo, targetWindow) ?? {};
    if (!callerInfo || !handler) {
        return;
    }
    
    handler({
        func: "updateCallerInfo",
        args: [callerInfo],
    });
    
    // Clear the callerInfo after sending it
    setCallerInfoState(undefined, mailboxInfo, targetWindow);
}

function setCallerInfoState(callerInfo: CallerInfo | undefined, mailboxInfo: MailboxInfo, targetWindow: Window, isImmersiveBizchat?: boolean): void {
    const state = getOrCreateState(mailboxInfo, targetWindow, isImmersiveBizchat);
    state.callerInfo = callerInfo;
}

function getOrCreateState(mailboxInfo: MailboxInfo, targetWindow: Window, isImmersiveBizchat?: boolean): WindowMessageState {
    const key = getMessageStateKey(mailboxInfo, targetWindow, isImmersiveBizchat);
    let state = messageStateMap.get(key);
    if (!state) {
        state = {
            accountKey: getAccountKeyForMailboxInfo(mailboxInfo),
            windowKey: getBizChatWindowKey(targetWindow, isImmersiveBizchat),
        };
        messageStateMap.set(key, state);
    }
    return state;
}

function getState(mailboxInfo: MailboxInfo, targetWindow: Window, isImmersiveBizchat?: boolean): WindowMessageState | undefined {
    const key = getMessageStateKey(mailboxInfo, targetWindow, isImmersiveBizchat);
    return messageStateMap.get(key);
}

function destroyState(mailboxInfo: MailboxInfo, targetWindow: Window, isImmersiveBizchat?: boolean): boolean {
    const key = getMessageStateKey(mailboxInfo, targetWindow, isImmersiveBizchat);
    return messageStateMap.delete(key);
}
